import { Breadcrumb, Button, Layout, Space, Typography } from 'antd';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddModal from './AddModal';
import AbaTypeEnum from '~Api/WarehouseLoanTransactionAba/AbaTypeEnum';
import ModalStateEnum from '~UI/ModalStateEnum';
import { IDictionary } from '~utilities/IDictionary';
import IWarehouseLoanTransactionAba from '~Api/WarehouseLoanTransactionAba/IWarehouseLoanTransactionAba';
import { useDispatch, useSelector } from 'react-redux';
import { abasSelector } from '~WarehouseLoanTransactionAbas/selectors';
import Table, { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { currencyFormatter } from '~utilities/formatters';
import _ from 'lodash';
import { Dispatch } from 'redux';
import { abasListAction } from '~WarehouseLoanTransactionAbas/actions';
import { Helmet } from 'react-helmet';
import abaTypeLabels from '../abaTypeLabels';
import { IGlobalState } from '~reducer';
import AbaTypeGroupEnum from '~WarehouseLoanTransactionAbas/AbaTypeGroupEnum';

export default function List(): ReactElement {
    const [ addModalState, setAddModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ addModalAbaType, setAddModalAbaType ] = useState<AbaTypeEnum>(null);

    const abas: IDictionary<IWarehouseLoanTransactionAba> = useSelector((state: IGlobalState) => abasSelector(state, AbaTypeGroupEnum.RepaymentPrincipal));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!abas) {
            dispatch(abasListAction(AbaTypeGroupEnum.RepaymentPrincipal));
        }
    }, [
        abas,
        dispatch,
    ]);

    const onClickFin: () => void = useCallback(() => {
        setAddModalAbaType(AbaTypeEnum.FinRepaymentPrincipal);
        setAddModalState(ModalStateEnum.Open);
    }, []);

    const onClickFit: () => void = useCallback(() => {
        setAddModalAbaType(AbaTypeEnum.FitRepaymentPrincipal);
        setAddModalState(ModalStateEnum.Open);
    }, []);

    const onClickFnom: () => void = useCallback(() => {
        setAddModalAbaType(AbaTypeEnum.FnomRepaymentPrincipal);
        setAddModalState(ModalStateEnum.Open);
    }, []);

    const onClickFwt1: () => void = useCallback(() => {
        setAddModalAbaType(AbaTypeEnum.Fwt1RepaymentPrincipal);
        setAddModalState(ModalStateEnum.Open);
    }, []);

    const onClickFwt2: () => void = useCallback(() => {
        setAddModalAbaType(AbaTypeEnum.Fwt2RepaymentPrincipal);
        setAddModalState(ModalStateEnum.Open);
    }, []);

    const onCloseAddModal: () => void = useCallback(() => {
        setAddModalState(ModalStateEnum.Closed);
    }, []);

    const columns: ColumnsType<IWarehouseLoanTransactionAba> = [
        {
            dataIndex: 'createdTime',
            defaultSortOrder: 'descend',
            render: (createdTime: string, warehouseLoanTransactionAba: IWarehouseLoanTransactionAba) => <Link to={`/principal-repayment-transfers/${warehouseLoanTransactionAba.uuid}/transactions`}>{dayjs(createdTime).format('DD/MM/YYYY HH:mm')}</Link>,
            sorter: (a: IWarehouseLoanTransactionAba, b: IWarehouseLoanTransactionAba) => dayjs(a.createdTime) > dayjs(b.createdTime) ? 1 : -1,
            title: 'Date',
        },
        {
            dataIndex: 'abaType',
            render: (abaType: AbaTypeEnum): ReactNode => abaTypeLabels[abaType],
            title: 'Destination',
            width: 200,
        },
        {
            dataIndex: 'transactionCount',
            title: 'Transaction Count',
            width: 200,
        },
        {
            dataIndex: 'amountPrincipalTotal',
            render: (amountPrincipalTotal: number) => currencyFormatter.format(amountPrincipalTotal),
            title: 'Principal Total',
            width: 220,
        },
    ];

    const addModalBlock: ReactNode = ModalStateEnum.Off !== addModalState && (
        <AddModal
            abaType={addModalAbaType}
            isOpen={ModalStateEnum.Open === addModalState}
            onClose={onCloseAddModal}
        />
    );

    return (
        <Layout className='principal-repayment-transfers'>
            <Helmet>
                <title>Principal Repayment Transfers</title>
            </Helmet>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/accounts'>Accounts</Link></Breadcrumb.Item>
                <Breadcrumb.Item>Principal Repayment Transfers</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Space className='actions'>
                        <Button onClick={onClickFin}>Add FIN Transfer</Button>
                        <Button onClick={onClickFit}>Add FIT Transfer</Button>
                        <Button onClick={onClickFnom}>Add FNOM Transfer</Button>
                        <Button onClick={onClickFwt1}>Add FWT1 Transfer</Button>
                        <Button onClick={onClickFwt2}>Add FWT2 Transfer</Button>
                    </Space>
                    <Typography.Title level={2}>Principal Repayment Transfers</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(abas)}
                        loading={!abas}
                        pagination={{ defaultPageSize: 50 }}
                        rowKey='uuid'
                        size='middle'
                    />
                    {addModalBlock}
                </Layout.Content>
            </Layout>
        </Layout>
    );
}
